import {
    faExclamationCircle,
    faHourglassClock,
} from '@fortawesome/pro-solid-svg-icons';
import { faBus, faPlane, faTrain } from '@fortawesome/pro-regular-svg-icons';
import type Trip from '../Trip';
import TripPackage from '../TripPackage';
import type TripSegment from '../TripSegment';
import BusPackage from '../bus/BusPackage';
import TrainPackage from '../train/TrainPackage';
import FlightPackage from '../flight/FlightPackage';
import RoutePriceClass from './RoutePriceClass';
import { RouteTripFactory } from './TripFactoryFactory';
import { type Highlight } from './types';
import ItineraryOrder from '@itinerary/ItineraryOrder';
import type TripPriceType from '@itinerary/PriceType';
import { ReserveType } from '@utils/itinerary';
import {
    type CancellationTerm,
    type CardLabel,
    type PassengerCount,
} from '@itinerary/types';
import { type JourneyDto } from '@clients/MultiRouteApi';

export class RoutePackage extends TripPackage<Trip, RoutePriceClass> {
    callToReserve = false;

    readonly _duration: number;

    hasStopAtNight = false;
    stopDurationSeconds = 0;

    override get duration(): number {
        return this._duration;
    }

    private readonly _singlePrice: number;

    get serviceLabel() {
        const flight = this.isFlightPackage() ? 'هواپیما' : '';
        const train = this.isTrainPackage() ? 'قطار' : '';
        const bus = this.isBusPackage() ? 'اتوبوس' : '';
        return [flight, train, bus].reduce(
            (prev, current) => {
                const test = { ...prev };
                if (current) {
                    test.count++;
                    switch (test.count) {
                        case 1:
                            test.label = current;
                            break;
                        case 2:
                            test.label = `${test.label} و ${current}`;
                            break;
                        case 3:
                            test.label = `${current}، ${test.label}`;
                            break;

                        default:
                            break;
                    }
                }
                return test;
            },
            {
                count: 0,
                label: '',
            }
        ).label;
    }

    override get totalCount(): number {
        return this.journeyPackages.reduce(
            (prev, { totalCount }) => Math.min(prev, totalCount),
            0
        );
    }

    override get departureDateTime(): Date {
        return this.journeyPackages.reduce(
            (prev, { departureDateTime }) =>
                dayConfig.gregory(departureDateTime).isBefore(prev)
                    ? departureDateTime
                    : prev,
            new Date(Date.now() + 1000 * 24 * 60 * 60 * 365 * 7)
        );
    }

    override get arrivalDateTime(): Date {
        return this.journeyPackages.reduce(
            (prev, { arrivalDateTime }) =>
                dayConfig.gregory(arrivalDateTime).isAfter(prev)
                    ? arrivalDateTime
                    : prev,
            new Date()
        );
    }

    points = null;

    override prices: TripPriceType<RoutePriceClass>[] = [];

    override get order(): ItineraryOrder {
        return new ItineraryOrder(
            this.departureDateTime.getTime(),
            this.defaultPrice?.singlePrice || 0
        );
    }

    key = '';

    override get reserveType(): ReserveType {
        return ReserveType.Route;
    }

    private readonly journeyPackages: TripPackage[] = [];

    get isAlternative() {
        return this.journeyPackages.length === 1;
    }

    static revive(data: RoutePackage) {
        const newPack = new RoutePackage();
        Object.assign(newPack, data);
        if (data.selectedPrices?.length) {
            newPack.selectedPrices = [];
            data.selectedPrices.forEach(price => {
                const priceClass = new RoutePriceClass({
                    packageId: price.packageId || '',
                    adultPrice: price.passengerFares[0].price,
                    maxCapacity: price.maxCapacity || 0,
                    termsAndConditions: [],
                    routePriceClasses: [],
                });
                Object.assign(priceClass, price);
                newPack.selectedPrices!.push(priceClass);
            });
        }
        newPack.segments[0].trips[0].departureDateTime = new Date(
            newPack.segments[0].trips[0].departureDateTime
        );
        newPack.journeyPackages.map(pkg => {
            switch (pkg.concreteType) {
                case ReserveType.Train:
                    return TrainPackage.revive(pkg as TrainPackage);
                case ReserveType.Flight:
                    return FlightPackage.revive(pkg as FlightPackage);
                case ReserveType.Bus:
                    return BusPackage.revive(pkg as BusPackage);
                default:
                    return undefined;
            }
        });
        return newPack;
    }

    get segments(): TripSegment[] {
        return this.journeyPackages.flatMap(({ segments }) => segments);
    }

    override get capacity() {
        return this.journeyPackages.reduce(
            (prev, next) => prev + next.capacity,
            0
        );
    }

    override get singlePrice() {
        return this._singlePrice;
    }

    override get singlePayable() {
        return this._singlePrice;
    }

    override isBusPackage(): this is BusPackage {
        return this.journeyPackages.some(
            pkg => pkg.reserveType === ReserveType.Bus
        );
    }

    override isTrainPackage(): this is TrainPackage {
        return this.journeyPackages.some(
            pkg => pkg.reserveType === ReserveType.Train
        );
    }

    override isFlightPackage(): this is FlightPackage {
        return this.journeyPackages.some(
            pkg => pkg.reserveType === ReserveType.Flight
        );
    }

    override get hasSeatSelection() {
        return this.journeyPackages
            .filter(pkg => pkg.isBusPackage())
            .some(pkg => pkg.hasSeatSelection);
    }

    override get showCapacityOnCard(): boolean {
        return false;
    }

    get highlight(): Highlight[] {
        return this.journeyPackages.flatMap((pkg, index) => {
            const havePreFix = index > 0;
            switch (pkg.reserveType) {
                case ReserveType.Flight:
                    return {
                        label: 'هواپیما',
                        icon: faPlane,
                        preFix: havePreFix ? '+' : undefined,
                    };
                case ReserveType.Train:
                    return {
                        label: 'قطار',
                        icon: faTrain,
                        preFix: havePreFix ? '+' : undefined,
                    };
                case ReserveType.Bus:
                    return {
                        label: 'اتوبوس',
                        icon: faBus,
                        preFix: havePreFix ? '+' : undefined,
                    };
                default:
                    return [];
            }
        });
    }

    override get labels(): CardLabel[] {
        const routeLabels: CardLabel[] = [];
        if (this.hasStopAtNight) {
            const nightStop: CardLabel = {
                title: 'توقف در شب',
                backgroundColor: 'var(--yellow-200)',
                color: 'var(--yellow-600)',
                icon: faExclamationCircle,
            };
            routeLabels.push(nightStop);
        }

        if (this.stopDurationSeconds) {
            const stopsDurationLabel: CardLabel = {
                title: `${formatTripDuration(this.stopDurationSeconds * 1000, true, true)} توقف`,
                backgroundColor: 'var(--cool-gray-100)',
                color: 'var(--cool-gray-700)',
                icon: faHourglassClock,
            };

            routeLabels.push(stopsDurationLabel);
        }

        return routeLabels;
    }

    override cancellationTerms: CancellationTerm[];

    constructor(journey?: JourneyDto, paxCount?: PassengerCount) {
        super();
        this._duration = journey?.durationSeconds ?? 0;
        this.hasStopAtNight = !!journey?.hasStopAtNight;
        this.stopDurationSeconds = journey?.stopDurationSeconds ?? 0;
        const tripFactory = new RouteTripFactory();
        this.concreteType = ReserveType.Route;
        this.journeyPackages =
            journey?.segments?.map(trip =>
                tripFactory.routeTripFactory(trip, paxCount)
            ) || [];
        this.cancellationTerms = this.journeyPackages.flatMap(
            pkg => pkg.cancellationTerms
        );

        this._singlePrice = journey?.price ?? 0;
        this.key = journey?.packageId ?? '';
        this.uniqueId = journey?.packageId || '';
        const price = new RoutePriceClass({
            packageId: journey?.packageId || '',
            routePriceClasses: this.journeyPackages
                .map(({ defaultPrice }) => defaultPrice)
                .filter(defaultPrice => !!defaultPrice),
            maxCapacity: this.totalCount,
            termsAndConditions:
                journey?.segments?.map(
                    ({ cancellationTerms, destinationTitle, originTitle }) => ({
                        destination: destinationTitle ?? '-',
                        origin: originTitle ?? '-',
                        term: cancellationTerms ?? '-',
                    })
                ) ?? [],
            adultPrice: journey?.price ?? 0,
        });
        this.prices = this.selectedPrices = [price];
        this.defaultPrice = this.selectedPrice = price;
    }

    override get tripTitle() {
        return `${this.firstTrip.origin.replace(/ *\([^)]*\)*/g, '')} به ${this.lastTrip.destination.replace(/ *\([^)]*\)*/g, '')}`;
    }
}
